import { Entity, FieldType, getRowDataFromKey, PersistenceType } from 'gossamer-unified';
import { UserView } from './UserView';
import { Institution } from './Institution';
import { InstitutionGroup } from './InstitutionGroup';

export interface ReportGroupUserViewRow {
  user_id: string;
  institution_id: number;
  institution_group_id: number;
  week_commencing: string;
  event_count: number; // total events
  post_event_count: number; // total events with flow_option IN 'attendanceOnly', 'full'
  booking_count: number;
  post_event_booking_count: number; // total bookings for above events
  booking_rate: number;
  attendance_count: number;
  attendance_rate: string;
  sum_content_score: number;
  avg_content_score: number;
  sum_delivery_score: number;
  avg_delivery_score: number;
  sum_admin_score: number;
  avg_admin_score: number;
  sum_overall_score: number;
  avg_overall_score: number;
  feedback_count: number;
}

export const ReportGroupUserView: Entity<ReportGroupUserViewRow> = {
  id: 'ReportGroupUserView',
  title: 'Report Group User View',
  service: 'main',
  rowLabel: (row: ReportGroupUserViewRow) => [row.user_id, row.week_commencing].join(' / '),
  primaryKeyFields: ['user_id', 'week_commencing'],
  source: {
    tableName: 'public.report_group_user_view',
  },
  security: [
    { role: 'user', action: 'read', access: 'conditional', condition: "user_id = '{userId}'" },
    { role: 'admin', action: 'read', access: 'conditional', condition: 'institution_id = {adminInstitutionId}' },
    { role: 'staff', action: 'read', access: 'all' },
  ],
  defaultSort: [['week_commencing', 'DESC']],
  defaultFilters: [
    { fieldId: 'user_id', operator: 'eq', value: '' },
    { fieldId: 'institution_group_id', operator: 'eq', value: '' },
    { fieldId: 'week_commencing', operator: 'eq', value: '' },
  ],
  listRowLink: (key: string) => {
    const { user_id, week_commencing } = getRowDataFromKey(ReportGroupUserView, key);
    const start = new Date(week_commencing as string);
    const end = new Date(week_commencing as string);
    end.setDate(end.getDate() + 7);
    return (
      '/admin/reporting/booking-search?q=' +
      encodeURIComponent(
        JSON.stringify({
          where: {
            and: [
              { fieldId: 'user_id', operator: 'eq', value: user_id },
              { fieldId: 'start_at', operator: 'gt', value: start.toISOString() },
              { fieldId: 'start_at', operator: 'lt', value: end.toISOString() },
            ],
          },
        }),
      )
    );
  },
  fields: {
    user_id: {
      seq: 5,
      label: 'User',
      type: FieldType.Reference,
      refEntity: UserView,
    },
    institution_id: {
      seq: 10,
      label: 'Institution',
      type: FieldType.Reference,
      refEntity: Institution,
    },
    institution_group_id: {
      seq: 15,
      label: 'Institution Group',
      type: FieldType.Reference,
      refEntity: InstitutionGroup,
    },
    week_commencing: {
      seq: 20,
      type: FieldType.Date,
      label: 'Week Commencing',
      listColumn: true,
      listColumnAlign: 'right',
    },
    event_count: {
      seq: 25,
      type: FieldType.Integer,
      label: 'All Events',
    },
    post_event_count: {
      seq: 30,
      type: FieldType.Integer,
      label: 'Tracked Events',
    },
    booking_count: {
      seq: 35,
      label: 'All Bookings',
      type: FieldType.Integer,
      listColumn: true,
    },
    booking_rate: {
      seq: 45,
      label: 'Booking Rate',
      type: FieldType.Decimal,
      decimalDigits: 2,
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportGroupUserViewRow) => (!row.event_count ? 0 : row.booking_count / row.event_count),
    },
    post_event_booking_count: {
      seq: 50,
      label: 'Tracked Bookings',
      type: FieldType.Integer,
      listColumn: true,
    },
    attendance_count: {
      seq: 60,
      label: 'Attendances',
      type: FieldType.Integer,
      listColumn: true,
    },
    attendance_rate: {
      seq: 65,
      label: 'Attendance Rate',
      type: FieldType.Text,
      listColumn: true,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportGroupUserViewRow) =>
        !row.post_event_booking_count
          ? '-'
          : ((row.attendance_count * 100) / row.post_event_booking_count).toFixed(0) + '%',
    },
    sum_content_score: {
      seq: 70,
      label: 'Total Content Score',
      type: FieldType.Decimal,
    },
    avg_content_score: {
      seq: 75,
      label: 'Average Content Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportGroupUserViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    sum_delivery_score: {
      seq: 80,
      label: 'Total Delivery Score',
      type: FieldType.Decimal,
    },
    avg_delivery_score: {
      seq: 85,
      label: 'Average Delivery Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportGroupUserViewRow) =>
        !row.feedback_count ? '-' : (row.sum_delivery_score / row.feedback_count).toFixed(1),
    },
    sum_admin_score: {
      seq: 90,
      label: 'Total Admin Score',
      type: FieldType.Decimal,
    },
    avg_admin_score: {
      seq: 95,
      label: 'Average Admin Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportGroupUserViewRow) =>
        !row.feedback_count ? '-' : (row.sum_admin_score / row.feedback_count).toFixed(1),
    },
    sum_overall_score: {
      seq: 100,
      label: 'Total Overall Score',
      type: FieldType.Decimal,
    },
    avg_overall_score: {
      seq: 105,
      label: 'Average Overall Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportGroupUserViewRow) =>
        !row.feedback_count ? '-' : (row.sum_overall_score / row.feedback_count).toFixed(1),
    },
    feedback_count: {
      seq: 110,
      label: 'Feedback',
      type: FieldType.Integer,
      listColumn: true,
    },
  },
};
